import { HttpClient, HttpClientModule } from '@angular/common/http';
import { Injectable, NgModule } from '@angular/core';
import { ToastrModule, ToastrService } from 'ngx-toastr';
import { BehaviorSubject, combineLatest, forkJoin, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Metadata, SpGetResponse, SpParams, SpPostHeaders, SpPostResponse } from '../models/share-point';

export type SharePointApiRoute = '_vti_bin/listdata.svc/' | '_api/web/';

export interface Message {
  title?: string;
  message?: string;
}

interface SpHeaders {
  Accept: 'application/json;odata=verbose' | 'application/json;odata=minimalmetadata' | 'application/json;odata=nometadata';
}

interface SpOnSuccess {
  onSuccess?: Message;
  hideOnSuccess?: boolean;
}

interface SpOnError {
  onError?: Message;
  hideOnErrors?: boolean;
}

interface SpOptions extends SpOnError {
  listRoute?: string;
  headers?: SpHeaders;
  useLegacyApi?: boolean;
  service?: boolean;
}

export interface SpGetOptions extends SpOptions {
  params?: SpParams;
}

export interface SpPostOptions extends SpOptions, SpOnSuccess {
  remove?: boolean;
  match?: string;
}

export interface SpPostAttachmentOptions extends SpOptions, SpOnSuccess { }

export class SpRequest {
  constructor(
    public listName: string,
    public options: SpGetOptions,
  ) { }
}

export interface SpBatchGetRequest {
  listName: string;
  options?: {
    params: SpParams;
    listRoute?: string;
  };
}

export interface SpBatchPostOptions extends SpPostOptions {
  listName?: string;
}

export interface SpBatchPostRequest {
  value: any;
  listName?: string;
  options?: {
    listRoute?: string;
    remove?: boolean;
    match?: string;
  };
}

export interface HasMetadata {
  __metadata?: Metadata;
}

interface ContextInfo {
  FormDigestTimeoutSeconds: number;
  FormDigestValue: string;
  LibraryVersion: string;
  SiteFullUrl: string;
  SupportedSchemaVersions: string[];
  WebFullUrl: string;
  'odata.metadata': string;
}

interface Digest {
  value: string;
  expirationTimestamp: number;
}

export interface Attachment {
  FileName: string;
  ServerRelativeUrl: string;
  __metadata: {
    id: string;
    uri: string;
    type: 'SP.Attachment';
  };
}

@Injectable({
  providedIn: 'root'
})
export class SharePointService {
  private _digestsByUrl = new Map<string, Digest>();

  constructor(
    private http: HttpClient,
    private toast: ToastrService,
  ) { }

  private _onError(error: any, { hideOnErrors, onError }: SpOnError = {}) {
    if (!hideOnErrors) {
      let spErrorMessage;

      try {
        spErrorMessage = error.error.error.message.value;
      } catch (error) { }

      const errorObj = onError || { message: spErrorMessage || '' } as Message;
      if (!this.toast.findDuplicate(errorObj.title, errorObj.message, true, false)) {
        this.toast.error(errorObj.message || '', errorObj.title || `Error ${error.status} - ${error.statusText}`);
      }
    }
    return throwError(error);
  }

  private _onSuccess({ onSuccess = {}, hideOnSuccess }: SpOnSuccess = {}) {
    if (!hideOnSuccess) {
      if (!this.toast.findDuplicate(onSuccess.title, onSuccess.message || '', true, false)) {
        this.toast.success(onSuccess.message || '', onSuccess.title || 'Updated');
      }
    }
  }

  private _spGet<T>(url: string, options: SpGetOptions = {}): Observable<T> {
    return this.http
      .get<T>(url, {
        headers: {
          ...{ 'Accept': 'application/json;odata=verbose' },
          ...options.headers,
        }
      })
      .pipe(catchError(error => this._onError(error, options)));
  }

  private _getRoute(listName: string, { listRoute = '', service, useLegacyApi }: SpOptions): string {
    if (useLegacyApi) {
      return `${listRoute}_vti_bin/listdata.svc/${listName}`;
    } else if (service) {
      return `${listRoute}_api/web/${listName}`;
    } else {
      return `${listRoute}_api/web/lists/GetByTitle('${listName}')/items`;
    }
  }

  private _createUri(req: SpRequest | string): string {
    if (req instanceof SpRequest) {
      let paramsString = '';
      if (req.options.params) {
        const params = req.options.params;
        for (const key in params) {
          if (params.hasOwnProperty(key)) {
            paramsString += `${(paramsString !== '') ? '&' : ''}${key}=${encodeURIComponent(params[key])}`;
          }
        }
      }
      return `${this._getRoute(req.listName, req.options)}?${paramsString}`;
    } else {
      return req.replace('https://spteam.aa.com', '');
    }
  }

  digest({ __metadata }: HasMetadata, { listRoute, useLegacyApi }: SpPostOptions = {}): Observable<string> {
    if (useLegacyApi) {
      return of(null);
    }

    let webUrl = '.';

    if (__metadata && __metadata.uri) {
      webUrl = __metadata.uri.substring(
        __metadata.uri.indexOf('.com') === -1 ? 0 : __metadata.uri.lastIndexOf('.com') + 4,
        __metadata.uri.lastIndexOf(__metadata.uri.indexOf('_api') > 0 ? '_api' : '_vti')
      );
    } else if (listRoute) {
      webUrl = listRoute;
    }

    const normalizedWebUrl = webUrl.replace(/\/$/, '');
    const cachedDigest = this._digestsByUrl.get(normalizedWebUrl);

    if (cachedDigest && new Date().getTime() < cachedDigest.expirationTimestamp) {
      return of(cachedDigest.value);
    } else {
      return this.http.post<ContextInfo>(`${normalizedWebUrl}${'/_api/contextinfo'}`, null).pipe(
        map((responseObject: any) => {
          const digestValue = responseObject.FormDigestValue;
          this._addDigestToCache(normalizedWebUrl, digestValue, responseObject.FormDigestTimeoutSeconds);
          return digestValue;
        })
      );
    }
  }

  private _addDigestToCache(webUrl: string, digestValue: string, seconds: number): void {
    const expirationTimeMs = new Date().getTime() + (1000 * seconds) - 15000;
    if (new Date().getTime() < expirationTimeMs) {
      this._digestsByUrl.set(webUrl, {
        value: digestValue,
        expirationTimestamp: expirationTimeMs
      });
    }
  }

  /**
   * Construct a SharePoint GET request using angular HttpClient and return only the results of the request as `T`.
   * @param listName as `string` name of SharePoint List
   * @param options as `SpGetOptions` options for params, and error messages
   * @return an `Observable` of the body as type `T[]`.
   */

  get<T>(listName: string, options: SpGetOptions = {}): Observable<T[]> {
    const req = new SpRequest(listName, options);
    const obs = new BehaviorSubject<SpRequest | string>(req);

    let combinedData: T[] = [];

    return obs.pipe(
      map(request => this._createUri(request)),
      switchMap(uri => this._spGet<SpGetResponse<T>>(uri, options)),
      map(response => {
        let data;
        if (response.d !== undefined) {
          data = response.d.results || response.d;
        } else if (response.value) {
          data = response.value;
        }

        combinedData = combinedData.concat(data);
        const topRequested = (options.params || { $top: undefined }).$top;

        if (topRequested === combinedData.length.toString()) {
          obs.complete();
        } else if ((response.d || { __next: null }).__next) {
          obs.next(response.d.__next);
        } else if (response['odata.nextLink']) {
          obs.next(response['odata.nextLink']);
        } else {
          obs.complete();
        }

        return combinedData;
      }),
    );
  }

  /**
   * Construct a SharePoint Post request using angular HttpClient and return only the results of the request as `T`.
   * @param listName as `string` name of SharePoint List
   * @param body as `any` body to be updated or removed
   * @param options as `SpPostOptions` options for error messages, success messages, and if the item should be removed
   * @return an `Observable` of the body as type `T`.
   */
  post<T extends HasMetadata>(listName: string, body: any, options: SpPostOptions = {}): Observable<T> {
    let uri: string = (body.__metadata || { uri: undefined }).uri || this._getRoute(listName, options);

    if (uri.indexOf('.com') > -1) {
      uri = uri.substring(uri.lastIndexOf('.com') + 4);
    }

    return this.digest(body, options).pipe(
      switchMap(digest => this.http.post<SpPostResponse<T>>(
        uri,
        JSON.stringify(this.cleanBody(listName, body, options)),
        new SpPostHeaders(body, options.remove, options.match, digest)
      )),
      map(response => (response ? response.d : undefined)),
      tap(() => this._onSuccess(options)),
      catchError(error => this._onError(error, options))
    );
  }

  attach(list: string, file: File, item: any, options: SpPostAttachmentOptions = {}): Observable<Attachment> {
    return combineLatest([
      this.fileReader(file),
      this.digest(item)
    ]).pipe(
      switchMap(([attachment, digest]) => {
        return this.http.post<{ d: Attachment }>(
          `${this._getRoute(list, options)}(${item.Id})/AttachmentFiles/add(FileName='${file.name}')`,
          attachment, {
          headers: {
            ...{
              'Accept': 'application/json; odata=verbose',
              'content-type': 'application/json; odata=verbose',
              'X-RequestDigest': digest
            },
            ...options.headers
          }
        });
      }),
      map(({ d }) => d),
      tap(() => this._onSuccess(options)),
      catchError(error => this._onError(error, options))
    );
  }

  attachMultiple(list: string, files: File[], item: any): Observable<Attachment[]> {
    const obsArray: Observable<Attachment>[] = []
    for (let i = 0; i < files.length; i++) {
      obsArray.push(this.attach(list, files[i], item));
    }
    return combineLatest(obsArray);
  }

  fileReader(attachment: File) {
    const subject = new Subject<string | ArrayBuffer | null>();
    const reader$ = new FileReader();

    reader$.onload = (chunk: any) => subject.next(chunk.target.result);
    reader$.onloadend = () => subject.complete();
    reader$.onerror = error => subject.error(error);

    reader$.readAsArrayBuffer(attachment);

    return subject;
  }

  /**
  * Cleans up any meta data that may cause issues when posinting to SharePoint and adds required data to meta data
  * @param listName as `string` name of SharePoint List
  * @param body as `any` body to be updated or removed
  * @param options as `SpPostOptions` options for error messages, success messages, and if the item should be removed
  * @return an copy of the body.
  */
  cleanBody(listName: string, body: any, { useLegacyApi }: SpPostOptions = {}) {
    let bodyCopy = JSON.parse(JSON.stringify(body));
    if (bodyCopy.__metadata) {
      for (const key in bodyCopy.__metadata) {
        if (key !== 'type' && bodyCopy.__metadata.hasOwnProperty(key)) {
          delete bodyCopy.__metadata[key];
        }
      }
    }

    if (!useLegacyApi) {
      bodyCopy = this._setType(bodyCopy, listName);
    }

    return bodyCopy;
  }

  /**
   * Construct a SharePoint GET request using angular HttpClient and return only the results of the request as `T`.
   * @param requests as `SpBatchGetRequest[]` name of SharePoint List
   * @param options as `SpGetOptions`
   * @return an `Observable` of the body as type `any[]`.
   */
  batchGet<T = any>(requests: SpBatchGetRequest[], options?: SpGetOptions): Observable<T[]> {
    const batches = this._chunkBatch<SpBatchGetRequest>(requests).map(batchRequests => {
      const batchContents = [];
      const changeSetId = this._generateUUID();
      const batchGuid = this._generateUUID();

      batchRequests.forEach(request => {
        const req = new SpRequest(request.listName, request.options);
        req.options.listRoute = req.options.listRoute || options.listRoute;
        const headers = {
          ...{ 'Accept': 'application/json;odata=verbose' },
          ...options.headers,
        };

        batchContents.push(`--batch_${batchGuid}`);
        batchContents.push(`Content-Type: application/http`);
        batchContents.push(`Content-Transfer-Encoding: binary`);
        batchContents.push(``);
        batchContents.push(`GET ${this._createUri(req)} HTTP/1.1`);
        batchContents.push(`Accept: ${headers.Accept}`);
        batchContents.push(``);
      });

      batchContents.push(`--batch_${batchGuid}--`);

      const batchBody = this._batchBody(batchContents, batchGuid, changeSetId);

      return this._batchRequest(batchBody, batchGuid, options);
    });

    return this._forkJoinBatches(batches);
  }

  /**
   * Construct a SharePoint GET request using angular HttpClient and return only the results of the request as `T`.
   * @param requests as `SpBatchPostRequest[]` name of SharePoint List
   * @param options as `SpGetOptions`
   * @return an `Observable` of the body as type `any[]`.
   */
  batchPost<T extends HasMetadata>(requests: SpBatchPostRequest[], options: SpBatchPostOptions = {}): Observable<T[]> {
    const batches = this._chunkBatch<SpBatchPostRequest>(requests).map(batchRequests => {
      const batchContents: string[] = [];
      const batchGuid = this._generateUUID();
      const changeSetId = this._generateUUID();

      requests.forEach(request => {
        request.options = request.options || {};
        const headers = new SpPostHeaders(request.value, request.options.remove, request.options.match).headers;
        const bodyItem = this._setType(request.value, request.listName || options.listName);

        batchContents.push(`--changeset_${changeSetId}`);
        batchContents.push(`Content-Type: application/http`);
        batchContents.push(`Content-Transfer-Encoding: binary`);
        batchContents.push(``);
        batchContents.push(`POST ${this._getRoute(request.listName || options.listName, { ...options, ...request.options })} HTTP/1.1`);
        // batchContents.push(`Content-Type: application/json;odata=verbose`);
        // batchContents.push(`Accept: application/json;odata=verbose`);
        for (const key in headers) {
          if (headers.hasOwnProperty(key)) {
            batchContents.push(`${key}: ${headers[key]}`);
          }
        }
        batchContents.push(``);
        batchContents.push(JSON.stringify(bodyItem));
        batchContents.push(``);
      });

      batchContents.push(`--changeset_${changeSetId}--`);

      const batchBody = this._batchBody(batchContents, batchGuid, changeSetId);

      options.hideOnSuccess = options.hideOnSuccess === undefined ? false : options.hideOnSuccess;

      return this._batchRequest(batchBody, batchGuid, options);
    });

    return this._forkJoinBatches(batches);
  }

  private _chunkBatch<T>(requests: T[]): T[][] {
    return requests.reduce((acc, request, index) => {
      const chunkIndex = Math.floor(index / 99);

      if (!acc[chunkIndex]) {
        acc[chunkIndex] = [request];
      } else {
        acc[chunkIndex].push(request);
      }

      return acc;
    }, [] as T[][]);
  }

  private _forkJoinBatches<T = any>(batches: Observable<any[]>[]): Observable<T[]> {
    return forkJoin(batches).pipe(
      map(results => {
        return results.reduce((acc, array) => {
          return acc.concat(array.reduce((rAcc, tasks) => rAcc.concat(tasks), []));
        }, [] as T[]);
      })
    );
  }

  private _batchBody(bodyArray: string[], batchGuid: string, changeSetId: string): string {
    const batch = [];
    const body = bodyArray.join('\r\n');

    batch.push(`--batch_${batchGuid}`);
    batch.push(`Content-Type: multipart/mixed; boundary="changeset_${changeSetId}"`);
    batch.push(`Content-Length: ${body.length}`);
    batch.push(`Content-Transfer-Encoding: binary`);
    batch.push(``);
    batch.push(body);
    batch.push(``);
    batch.push(`--batch_${batchGuid}--`);
    return batch.join('\r\n');
  }

  private _batchRequest(body: string, batchGuid: string, options: SpPostOptions = {}): Observable<any[]> {
    options.hideOnSuccess = options.hideOnSuccess === undefined ? true : options.hideOnSuccess;

    const uri: string = (options || { listRoute: '' }).listRoute || '';

    return this.digest({ __metadata: { uri: uri } } as HasMetadata, options).pipe(
      switchMap(digest => this.http.post(`${uri}_api/$batch`, body, {
        responseType: 'text',
        headers: {
          'Content-Type': `multipart/mixed; boundary="batch_${batchGuid}"`,
          'X-RequestDigest': digest || 'no_digest'
        }
      })),
      map(response => this._parseBatchResponse(response)),
      tap(() => this._onSuccess(options)),
      catchError(error => this._onError(error, options))
    );
  }

  private _parseBatchResponse(response: string): any[] {
    return response.split('\n')
      .map(stringElement => {
        try {
          const obj = JSON.parse(stringElement);
          return obj.d ? obj.d.results || obj.d : obj;
        } catch { }
      })
      .filter(parsedElement => parsedElement !== undefined);
  }

  private _setType<T extends { __metadata?: { type: string; } }>(item: T, listName: string): T {
    if (!item.__metadata) {
      item.__metadata = {
        type: this._getItemType(listName)
      };
    }
    return item;
  }

  private _getItemType(listName: string): string {
    return `SP.Data.${listName}ListItem`.replace(/_/g, '_x005f_').replace(/ /g, '_x0020_');
  }

  private _generateUUID() {
    let d = new Date().getTime();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      // tslint:disable-next-line:no-bitwise
      const r = (d + Math.random() * 16) % 16 | 0;
      d = Math.floor(d / 16);
      // tslint:disable-next-line:no-bitwise
      return (c === 'x' ? r : (r & 0x7 | 0x8)).toString(16);
    });
  }
}

@NgModule({
  imports: [
    HttpClientModule,
    ToastrModule.forRoot({
      positionClass: 'toast-bottom-right',
      preventDuplicates: true,
      resetTimeoutOnDuplicate: true,
    }),
  ],
  providers: [
    SharePointService,
  ],
  exports: [ToastrModule]
})
export class SharePointModule { }
